import { useContext } from "react";

import { useTranslation } from "react-i18next";

import { css } from "@emotion/react";

import {
    Badge,
    BadgeColors,
    colors,
    TaskInfoOutlineIcon
} from "@dotnet/telematics-design-system";

import InlineActionIconButton from "components/DesignComponents/Button/InlineActionIconButton";
import TableContext from "context/table/tableContext";
import { body4SemiBold, body4SemiBoldStyles } from "shared/styles/typography";

import { TableItemInstanceProps } from "../types";
import ApproveDiscard from "./ApproveDiscard";
import TaskStats from "./TaskStats";

const MobileCard = ({
    data,
    isApprovalRequired,
    isApprovalBadgeRendered
}: TableItemInstanceProps) => {
    const { gray200, gray700, gray900, white } = colors;

    const { tableName, setDetailsData } = useContext(TableContext);

    const { t } = useTranslation();

    const {
        id,
        company_name,
        task_type,
        type,
        task_file_name,
        objects,
        created_at,
        created_by,
        task_stats
    } = data;

    const handleDetailsClick = () => setDetailsData(data);

    return (
        <div
            css={css({
                background: white
            })}
        >
            <div
                css={css({
                    "& > div": {
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "center",
                        height: "56px",
                        boxSizing: "border-box",
                        borderBottom: `1px solid ${gray200}`,
                        padding: "0 24px",

                        "& > div:first-of-type": {
                            ...body4SemiBoldStyles,
                            color: gray700
                        },

                        "& > div:last-of-type": {
                            color: gray900,
                            fontSize: "14px",
                            fontWeight: "500",
                            lineHeight: "20px",
                            letterSpacing: "0.1px",
                            whiteSpace: "nowrap",
                            overflowX: "hidden",
                            textOverflow: "ellipsis"
                        }
                    }
                })}
            >
                <div>
                    <div>{t("General##task type")}</div>

                    <div>
                        {task_type
                            ? t(`General##${task_type}`)
                            : t(`Task groups##bulk type##${type}`)}
                    </div>
                </div>

                {task_file_name && (
                    <div>
                        <div>{t("General##file")}</div>
                        <div>{task_file_name}</div>
                    </div>
                )}

                <div>
                    <div>{t("General##id")}</div>
                    <div>{id}</div>
                </div>

                <div>
                    <div>{t("General##company")}</div>
                    <div>{company_name}</div>
                </div>

                <div>
                    <div>{t("General##created at")}</div>
                    <div>{created_at}</div>
                </div>

                {created_by && (
                    <div>
                        <div>{t("General##created by")}</div>
                        <div>{created_by.email}</div>
                    </div>
                )}
            </div>

            {(isApprovalRequired || task_stats.length > 0) && (
                <div
                    css={css({
                        padding: "9px 24px"
                    })}
                >
                    <div
                        css={[
                            body4SemiBold,
                            { color: gray700, marginBottom: "2px" }
                        ]}
                    >
                        {t("General##status")}
                    </div>

                    {isApprovalBadgeRendered ? (
                        <div>
                            <Badge color={BadgeColors.light}>
                                {objects} {t("General##Pending approval")}
                            </Badge>
                        </div>
                    ) : (
                        <TaskStats data={task_stats} />
                    )}
                </div>
            )}

            <div
                css={css({
                    display: "flex",
                    padding: "8px 24px",
                    borderTop: `1px solid ${gray200}`,

                    "& > span": {
                        display: "inline-flex",
                        width: "100%"
                    },

                    "& > span + span": {
                        marginLeft: "16px"
                    }
                })}
            >
                {isApprovalRequired && <ApproveDiscard id={id} />}

                <InlineActionIconButton
                    title={t(`Table##show details##${tableName}`)}
                    icon={<TaskInfoOutlineIcon />}
                    onButtonClick={handleDetailsClick}
                />
            </div>
        </div>
    );
};

export default MobileCard;
